import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap, useOrg, type UploadRule } from "@vixio/data";
import { registerAuditLabels, type StatusMap } from "@vixio/ui";
import type { Segment } from "@vixio/transcription/segments";
import "@vixio/ai_gateway-ui";

/** Tipos das linhas e RPCs do módulo transcription (ver modules/transcription/README.md). */

export type JobStatus = "aguardando_envio" | "na_fila" | "processando" | "concluido" | "falhou" | "cancelado" | "expirado";
export type Stage = "preparando" | "extraindo_audio" | "transcrevendo" | "separando_falantes" | "finalizando";
export type Language = "pt" | "en" | "es" | "auto";
export type Diarization = "auto" | "local" | "desligada";
export type ConsentBasis = "consentimento" | "aviso" | "gravacao_propria";
export type Family = "local" | "nuvem" | "importacao";

export const JOB_STATUS: StatusMap = {
  aguardando_envio: { label: "enviando arquivo", tone: "info" },
  na_fila: { label: "na fila", tone: "info" },
  processando: { label: "transcrevendo", tone: "info" },
  concluido: { label: "pronta", tone: "ok" },
  falhou: { label: "falhou", tone: "crit" },
  cancelado: { label: "cancelada", tone: "neutral" },
  expirado: { label: "expirada", tone: "neutral" },
};

export const STAGES: Array<{ key: Stage; label: string }> = [
  { key: "preparando", label: "Preparando o arquivo" },
  { key: "extraindo_audio", label: "Extraindo o áudio" },
  { key: "transcrevendo", label: "Transcrevendo" },
  { key: "separando_falantes", label: "Separando quem falou" },
  { key: "finalizando", label: "Finalizando" },
];

export const LANGUAGES: Array<{ value: Language; label: string }> = [
  { value: "pt", label: "Português" },
  { value: "en", label: "Inglês" },
  { value: "es", label: "Espanhol" },
  { value: "auto", label: "Detectar automaticamente" },
];

export const DIARIZATION: Array<{ value: Diarization; label: string; hint: string }> = [
  { value: "auto", label: "Automática", hint: "Usa a separação do próprio motor quando ele faz; senão, separa no servidor da Vixio." },
  { value: "local", label: "No servidor da Vixio", hint: "Sempre separa os falantes aqui, sem enviar nada (sherpa-onnx)." },
  { value: "desligada", label: "Não separar", hint: "Só o texto, sem identificar quem falou." },
];

export const CONSENT: Array<{ value: ConsentBasis; label: string; hint: string }> = [
  { value: "consentimento", label: "Os participantes autorizaram a gravação", hint: "Consentimento (LGPD, art. 7º, I), por exemplo pelo link de gravação da agenda." },
  { value: "aviso", label: "Os participantes foram avisados da gravação e da finalidade", hint: "Aviso no início da reunião ou no convite, com a finalidade (ata e encaminhamentos)." },
  { value: "gravacao_propria", label: "É uma gravação só minha (ditado, anotação de voz)", hint: "Sem outras pessoas na gravação." },
];

export const FAMILY_LABEL: Record<Family, string> = { local: "No servidor", nuvem: "Serviço externo", importacao: "Importação" };

export interface EngineOption {
  id: string | null;
  kind: string;
  label: string;
  model: string | null;
  family: Family;
  audio_leaves: boolean;
  diarization: boolean;
  default: boolean;
}

export interface TranscriptionConfig {
  enabled: boolean;
  engines: EngineOption[];
  defaults: { language: Language; diarization: Diarization; max_speakers: number | null; require_consent: boolean; auto_minutes: boolean };
  limits: { max_file_mb: number; max_duration_minutes: number; media_retention_days: number; keep_original: boolean };
  usage: { minutes: number; quota: number | null };
  ai_available: boolean;
  meet_available: boolean;
}

export interface EngineKind {
  key: string;
  label: string;
  description: string;
  family: Family;
  protocol: string;
  default_base_url: string | null;
  default_model: string | null;
  models: string[];
  diarization: boolean;
  word_timestamps: boolean;
  audio_leaves: boolean;
  needs_key: boolean;
  whole_file: boolean;
  max_upload_mb: number | null;
  needs_region: boolean;
  docs_url: string | null;
  sort: number;
}

export interface EngineRow {
  id: string;
  org_id: string;
  kind: string;
  label: string;
  base_url: string | null;
  model: string | null;
  region: string | null;
  options: { prompt?: string; temperature?: number; keyterms?: string; extra?: Record<string, string | number | boolean> };
  audio_leaves: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface TranscriptionSettings {
  org_id: string;
  default_engine_id: string | null;
  local_kind: "whisper_local" | "speaches" | "whisper_cpp" | "whisper_asr";
  local_url: string;
  local_model: string;
  language: Language;
  diarization: Diarization;
  max_speakers: number | null;
  require_consent: boolean;
  keep_original: boolean;
  media_retention_days: number;
  max_file_mb: number;
  max_duration_minutes: number;
  monthly_quota_minutes: number | null;
  chunk_seconds: number;
  timeout_minutes: number;
  auto_minutes: boolean;
  notify_on_done: boolean;
  configured: boolean;
  usage_minutes: number;
  updated_at: string;
}

export interface JobRow {
  id: string;
  org_id: string;
  title: string;
  visibility: "equipe" | "restrita";
  origin_type: string | null;
  origin_id: string | null;
  source: "envio" | "google_meet";
  input_kind: "midia" | "legenda" | "transcricao_meet";
  engine_id: string | null;
  engine_kind: string;
  engine_kind_label: string;
  engine_family: Family;
  engine_label: string;
  engine_model: string | null;
  audio_leaves: boolean;
  language: Language;
  diarization: Diarization;
  speakers_hint: number | null;
  consent_basis: ConsentBasis | null;
  file_name: string | null;
  content_type: string | null;
  size_bytes: number | null;
  duration_seconds: number | null;
  status: JobStatus;
  stage: Stage | null;
  progress: number;
  stage_detail: string | null;
  error: string | null;
  attempts: number;
  max_attempts: number;
  current_version: number | null;
  requested_by: string;
  requested_by_email: string | null;
  created_at: string;
  submitted_at: string | null;
  started_at: string | null;
  done_at: string | null;
  media_purge_at: string | null;
  media_purged_at: string | null;
  erased_at: string | null;
  audio_available: boolean;
  segment_count: number | null;
  word_count: number | null;
  speakers: number;
}

export interface TranscriptRow {
  id: string;
  job_id: string;
  version: number;
  source: "motor" | "importacao" | "edicao";
  language: string | null;
  engine_kind: string | null;
  engine_model: string | null;
  segments: Segment[];
  segment_count: number;
  word_count: number;
  duration_seconds: number | null;
  content_sha256: string;
  note: string | null;
  created_at: string;
  created_by: string | null;
}

export interface SpeakerRow {
  id: string;
  job_id: string;
  key: string;
  name: string | null;
  seconds: number;
}

export interface JobEventRow {
  id: number;
  job_id: string;
  kind: string;
  data: Record<string, unknown>;
  actor: string | null;
  at: string;
}

export interface UsageRow {
  org_id: string;
  month: string;
  engine_kind: string;
  engine_kind_label: string;
  audio_leaves: boolean;
  jobs: number;
  done: number;
  failed: number;
  minutes: number;
}

export interface MeetItem {
  id: string;
  org_id: string;
  owner_user: string;
  connection_id: string;
  kind: "gravacao" | "transcricao";
  artifact: string;
  conference_record: string;
  meeting_code: string | null;
  title: string | null;
  started_at: string | null;
  ended_at: string | null;
  state: "pronto" | "processando" | "indisponivel";
  drive_file_id: string | null;
  docs_document_id: string | null;
  entries_until: string | null;
  job_id: string | null;
  seen_at: string;
}

export interface MeetSyncState {
  org_id: string;
  owner_user: string;
  requested_at: string | null;
  started_at: string | null;
  synced_at: string | null;
  items: number;
  last_error: string | null;
}

export interface RequestOptions {
  engine_id?: string | null;
  language?: Language;
  diarization?: Diarization;
  speakers?: number | null;
  prompt?: string | null;
  visibility?: "equipe" | "restrita";
  origin_type?: string | null;
  origin_id?: string | null;
  consent_basis?: ConsentBasis | null;
  consent_note?: string | null;
  accept_external?: boolean;
}

export interface RequestResult {
  job_id: string;
  status: JobStatus;
  bucket: string;
  path: string;
  content_type: string;
  input: "midia" | "legenda";
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "transcription.config": { args: { p_org: U }; returns: TranscriptionConfig };
    "transcription.get_settings": { args: { p_org: U }; returns: TranscriptionSettings };
    "transcription.set_settings": { args: { p_org: U; p_settings: Partial<Omit<TranscriptionSettings, "org_id" | "configured" | "usage_minutes" | "updated_at">> }; returns: TranscriptionSettings };
    "transcription.save_engine": {
      args: { p_org: U; p_engine: U | null; p_data: Partial<Pick<EngineRow, "kind" | "label" | "base_url" | "model" | "region" | "options" | "audio_leaves" | "enabled">> };
      returns: EngineRow & { secret_key: string };
    };
    "transcription.delete_engine": { args: { p_engine: U }; returns: "desligado" | "removido" };
    "transcription.request_job": { args: { p_org: U; p_title: string; p_file: { name: string; size: number }; p_options?: RequestOptions }; returns: RequestResult };
    "transcription.confirm_upload": { args: { p_job: U }; returns: { job_id: U; status: JobStatus } };
    "transcription.cancel_job": { args: { p_job: U; p_reason?: string | null }; returns: null };
    "transcription.retry_job": { args: { p_job: U; p_options?: Pick<RequestOptions, "engine_id" | "language" | "diarization" | "speakers" | "prompt" | "accept_external"> }; returns: null };
    "transcription.update_job": { args: { p_job: U; p_data: { title?: string; visibility?: "equipe" | "restrita"; origin_type?: string | null; origin_id?: string | null } }; returns: null };
    "transcription.media_link": { args: { p_job: U }; returns: { bucket: string; path: string; content_type: string; expires_at: string | null } };
    "transcription.save_version": { args: { p_job: U; p_segments: Segment[]; p_base_version: number; p_note?: string | null }; returns: { job_id: U; version: number; changed: boolean } };
    "transcription.rename_speaker": { args: { p_job: U; p_key: string; p_name: string | null }; returns: null };
    "transcription.log_export": { args: { p_job: U; p_format: string }; returns: null };
    "transcription.request_minutes": { args: { p_job: U }; returns: { run_id: U; status: string; idempotent: boolean; truncated: boolean; version: number; error?: string | null } };
    "transcription.purge_media_now": { args: { p_job: U }; returns: null };
    "transcription.erase_job": { args: { p_job: U; p_reason?: string | null }; returns: null };
    "transcription.request_meet_sync": { args: { p_org: U }; returns: null };
    "transcription.request_meet_import": { args: { p_item: U; p_options?: RequestOptions }; returns: { job_id: U; status: JobStatus; input: string } };
    "integrations.connect_my_google_meet": { args: { p_org: U }; returns: { connection_id: U; redirect_uri: string; auth_url: string } };
  }
}

registerAuditLabels({
  "transcription.job_requested": "Transcrição pedida",
  "transcription.job_canceled": "Transcrição cancelada",
  "transcription.job_retried": "Transcrição reprocessada",
  "transcription.job_failed": "Transcrição falhou",
  "transcription.job_updated": "Transcrição alterada",
  "transcription.version_saved": "Transcrição editada (nova versão)",
  "transcription.media_purge_requested": "Áudio da transcrição apagado a pedido",
  "transcription.job_erased": "Transcrição apagada (LGPD)",
  "transcription.settings_updated": "Configurações da transcrição alteradas",
  "transcription.engine_created": "Motor de transcrição cadastrado",
  "transcription.engine_updated": "Motor de transcrição alterado",
  "transcription.engine_disabled": "Motor de transcrição desligado",
  "transcription.engine_deleted": "Motor de transcrição removido",
});

const MB = 1024 * 1024;
const MEDIA_TYPES: Record<string, string> = {
  mp3: "audio/mpeg", m4a: "audio/mp4", aac: "audio/aac", ogg: "audio/ogg", oga: "audio/ogg", opus: "audio/opus", wav: "audio/wav",
  flac: "audio/flac", weba: "audio/webm", amr: "audio/amr", wma: "audio/x-ms-wma", mp4: "video/mp4", m4v: "video/mp4",
  webm: "video/webm", mov: "video/quicktime", mkv: "video/x-matroska", avi: "video/x-msvideo", "3gp": "video/3gpp",
  vtt: "text/vtt", srt: "application/x-subrip", txt: "text/plain",
};

/** Arquivos aceitos (o banco confere de novo, pela extensão; o worker, pelos bytes). */
export function mediaRule(maxMb: number): UploadRule {
  return {
    types: MEDIA_TYPES,
    maxBytes: maxMb * MB,
    label: "áudio (MP3, M4A, WAV, OGG, OPUS, FLAC), vídeo (MP4, MOV, MKV, WEBM) ou legenda (VTT, SRT, TXT)",
  };
}

export const isSubtitle = (name: string) => /\.(vtt|srt|txt)$/i.test(name);

/**
 * Vixio Transcritor (PWA em /transcritor/, apps/os): o Whisper e a separação de falantes rodam no computador de quem
 * usa e só o texto volta para cá. Com a reunião de origem, a transcrição enviada fica ligada a ela.
 */
export function transcritorUrl(origin?: { type: "agenda.meeting"; id: string; title: string } | null): string {
  if (!origin) return "/transcritor/";
  const q = new URLSearchParams({ origem: `${origin.type}:${origin.id}`, titulo: origin.title.slice(0, 200) });
  return `/transcritor/?${q.toString()}`;
}

const RUNNING: JobStatus[] = ["aguardando_envio", "na_fila", "processando"];
export const isRunning = (s: JobStatus) => RUNNING.includes(s);

export function useTranscriptionConfig() {
  const org = useOrg();
  return useQuery({ queryKey: ["transcription", "config", org.id], queryFn: () => rpc("transcription", "config", { p_org: org.id }) });
}

/** Transcrições recentes (a RLS decide: equipe vê as da equipe; restritas, só quem pediu e administradores). */
export function useJobs(opts: { originId?: string | null; limit?: number } = {}) {
  const org = useOrg();
  const limit = opts.limit ?? 200;
  return useQuery({
    queryKey: ["transcription", "jobs", org.id, opts.originId ?? "", limit],
    queryFn: () => {
      let q = from("transcription", "v_jobs").select("*").eq("org_id", org.id);
      if (opts.originId) q = q.eq("origin_id", opts.originId);
      return unwrap<JobRow[]>(q.order("created_at", { ascending: false }).limit(limit));
    },
    refetchInterval: (q) => ((q.state.data ?? []).some((j) => isRunning(j.status)) ? 3000 : false),
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: ["transcription", "job", id],
    enabled: Boolean(id),
    queryFn: async () => (await unwrap<JobRow[]>(from("transcription", "v_jobs").select("*").eq("id", id!).limit(1)))[0] ?? null,
    refetchInterval: (q) => (q.state.data && isRunning(q.state.data.status) ? 2000 : false),
  });
}

export function useTranscript(jobId: string | undefined, version: number | null | undefined) {
  return useQuery({
    queryKey: ["transcription", "transcript", jobId, version],
    enabled: Boolean(jobId && version),
    queryFn: async () => (await unwrap<TranscriptRow[]>(from("transcription", "transcripts").select("*").eq("job_id", jobId!).eq("version", version!).limit(1)))[0] ?? null,
    staleTime: Infinity,
  });
}

// Versões, falantes e histórico levam o estado do trabalho na chave (`stamp`): quando o worker conclui, salva uma
// versão ou muda de etapa, a tela busca de novo sem esperar recarregar a página.
export function useVersions(jobId: string | undefined, stamp?: unknown) {
  return useQuery({
    queryKey: ["transcription", "versions", jobId, stamp ?? null],
    enabled: Boolean(jobId),
    queryFn: () => unwrap<Array<Pick<TranscriptRow, "version" | "source" | "note" | "created_at" | "created_by" | "segment_count" | "word_count">>>(
      from("transcription", "transcripts").select("version, source, note, created_at, created_by, segment_count, word_count").eq("job_id", jobId!).order("version", { ascending: false })),
  });
}

export function useSpeakers(jobId: string | undefined, stamp?: unknown) {
  return useQuery({
    queryKey: ["transcription", "speakers", jobId, stamp ?? null],
    enabled: Boolean(jobId),
    queryFn: () => unwrap<SpeakerRow[]>(from("transcription", "speakers").select("*").eq("job_id", jobId!).order("key")),
  });
}

export function useJobEvents(jobId: string | undefined, stamp?: unknown) {
  return useQuery({
    queryKey: ["transcription", "events", jobId, stamp ?? null],
    enabled: Boolean(jobId),
    queryFn: () => unwrap<JobEventRow[]>(from("transcription", "job_events").select("*").eq("job_id", jobId!).order("id", { ascending: false }).limit(100)),
  });
}

/** Cor estável de cada falante (S1, S2...). */
export const SPEAKER_COLORS = ["#2563eb", "#d97706", "#059669", "#db2777", "#7c3aed", "#0891b2", "#dc2626", "#65a30d", "#9333ea", "#0d9488"];
export function speakerColor(key: string | null | undefined): string {
  if (!key) return "var(--text-3, #64748b)";
  const n = Number(key.replace(/^S/, "")) || 1;
  return SPEAKER_COLORS[(n - 1) % SPEAKER_COLORS.length]!;
}

export const EVENT_LABEL: Record<string, string> = {
  solicitado: "Pedida", enviado: "Arquivo enviado", reservado: "Worker começou", etapa: "Etapa", concluido: "Concluída",
  nova_tentativa: "Nova tentativa agendada", falhou: "Falhou", cancelado: "Cancelada", reprocessado: "Reprocessada",
  expirado: "Expirou sem envio", original_apagado: "Arquivo original apagado", midia_apagada: "Áudio apagado (retenção)",
  editado: "Editada (nova versão)", falante_renomeado: "Falante renomeado", midia_ouvida: "Áudio aberto", exportado: "Exportada",
  ata_solicitada: "Ata pedida à IA", apagado: "Conteúdo apagado (LGPD)",
};

export const SOURCE_LABEL: Record<TranscriptRow["source"], string> = { motor: "Transcrição automática", importacao: "Importada", edicao: "Edição" };

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "-";
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${String(m).padStart(2, "0")} min` : m ? `${m} min ${String(s % 60).padStart(2, "0")} s` : `${s} s`;
}
